"use client"
import { useEffect, useMemo, useSyncExternalStore } from "react"
import { refreshSyncProjection } from "@/actions/sync-projections"
import { ProjectionCollection, compareRevision, type ShapeMessage } from "./collection"
import { loadProjection, saveProjection, clearSavedProjections } from "./persistence"
import { SYNC_SURFACES, syncCookie, type SyncManifest, type SyncSnapshot } from "./types"
import { useReportSyncStatus } from "./status"

const stores = new Map<string, ProjectionStore>()
let activeViewer: string | null = null
export async function clearSyncData() {
  for (const store of stores.values()) store.deny()
  stores.clear(); activeViewer = null
  for (const surface of SYNC_SURFACES) document.cookie = `${syncCookie(surface)}=; Max-Age=0; Path=/; SameSite=Strict`
  await clearSavedProjections().catch(() => {})
}

export class ProjectionStore extends ProjectionCollection {
  listeners = new Set<() => void>()
  references = 0
  controller: AbortController | null = null
  offset = "-1"
  handle: string | null = null
  cursor: string | null = null
  private acknowledged = { offset: "-1", handle: null as string | null, cursor: null as string | null }
  private timer: ReturnType<typeof setTimeout> | null = null
  private writing: Promise<unknown> = Promise.resolve()
  private epoch = 0
  constructor(manifest: SyncManifest, initial?: SyncSnapshot) { super(manifest, initial) }
  get key() { return `${this.state.manifest.viewer}:${this.state.manifest.scope}` }
  snapshot = () => this.state
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => this.listeners.delete(fn) }
  emit() { for (const listener of this.listeners) listener() }
  status(status: Parameters<ProjectionCollection["setStatus"]>[0]) { if (status !== this.state.status) { this.setStatus(status); this.emit() } }
  deny() { this.epoch++; super.deny(); this.controller?.abort(); this.emit(); void clearSavedProjections().catch(() => {}) }
  private base(path: string) {
    const m = this.state.manifest
    const url = new URL(path, location.origin)
    for (const key of ["surface", "scope", "viewer", "companyId"] as const) if (m[key]) url.searchParams.set(key, m[key]!)
    return url
  }
  private persist() {
    const epoch = this.epoch
    const key = this.key
    const value = { manifest: this.state.manifest, rows: this.state.rows, offset: this.offset,
      handle: this.handle, cursor: this.cursor, savedAt: Date.now() }
    // Serialize writes: an older IndexedDB transaction must not overwrite a
    // newer acknowledged offset, and never persist a partially read chunk.
    this.writing = this.writing.catch(() => {}).then(() => epoch === this.epoch && this.state.status !== "denied" ? saveProjection(key, value) : undefined).catch(() => {})
    document.cookie = `${syncCookie(value.manifest.surface)}=${value.manifest.viewer}.${value.manifest.scope}; Path=/; Max-Age=86400; SameSite=Strict; Secure`
  }
  private async fallback(signal: AbortSignal) {
    const m = this.state.manifest
    const response = await fetch(this.base("/api/sync/snapshot"), { signal, cache: "no-store",
      headers: this.state.complete ? { "if-none-match": `"${m.scope}:${m.revision}"` } : {} })
    if ([401, 403, 409].includes(response.status)) { this.deny(); return }
    if (response.status === 304) return
    if (!response.ok) throw new Error("Snapshot unavailable")
    this.seed(await response.json() as SyncSnapshot)
    this.offset = "-1"; this.handle = null; this.cursor = null
    this.acknowledged = { offset: "-1", handle: null, cursor: null }
    this.emit(); this.persist()
  }
  retain() {
    if (++this.references === 1) void this.run()
    return () => {
      if (--this.references === 0) { this.controller?.abort(); if (this.timer) clearTimeout(this.timer) }
    }
  }
  reconcile = () => {
    if (this.timer) clearTimeout(this.timer)
    this.status("updating")
    this.timer = setTimeout(() => {
      this.timer = null
      const m = this.state.manifest
      void refreshSyncProjection(m.surface, m.scope, m.companyId).then(() => {
        this.controller?.abort(); if (this.references) void this.run()
      }).catch(error => {
        if (error instanceof Error && error.message === "Access changed") this.deny()
        else this.status("paused")
      })
    }, 400)
  }
  private async run() {
    const ac = new AbortController(); this.controller = ac
    // Discard unacknowledged chunks when a poll is interrupted. Only a complete
    // collection can be paired with an offset and replayed safely.
    Object.assign(this, this.acknowledged)
    try {
      const saved = await loadProjection(this.key).catch(() => undefined)
      if (ac.signal.aborted) return
      if (saved && Date.now() - saved.savedAt < 86_400_000 && (!this.state.complete || compareRevision(saved.manifest.revision, this.state.manifest.revision) >= 0)) {
        this.seed(saved); this.offset = saved.offset; this.handle = saved.handle; this.cursor = saved.cursor
        this.acknowledged = { offset: this.offset, handle: this.handle, cursor: this.cursor }
        this.emit()
      }
      if (this.offset !== "-1" && this.state.complete) this.resume(); else this.reset()
      let synced = this.offset !== "-1", failures = 0
      while (!ac.signal.aborted && this.references && this.state.status !== "denied") {
        const url = this.base("/api/shapes/screens")
        url.searchParams.set("offset", this.offset)
        if (this.handle) url.searchParams.set("handle", this.handle)
        if (this.cursor) url.searchParams.set("cursor", this.cursor)
        if (synced) url.searchParams.set("live", "true")
        try {
          const response = await fetch(url, { signal: ac.signal, cache: "no-store" })
          if (response.status === 409 && response.headers.get("electric-handle")) {
            this.handle = response.headers.get("electric-handle"); this.offset = "-1"; this.cursor = null
            this.reset(); synced = false; this.status("connecting"); continue
          }
          if ([401, 403, 409].includes(response.status)) { this.deny(); return }
          if (!response.ok) throw new Error("Sync unavailable")
          const messages = await response.json() as ShapeMessage[]
          if (ac.signal.aborted) return
          const before = this.state
          const result = this.consume(messages)
          if (result === "reset") {
            this.offset = "-1"; this.handle = null; this.cursor = null; synced = false; this.status("connecting"); continue
          }
          this.handle = response.headers.get("electric-handle") ?? this.handle
          this.offset = response.headers.get("electric-offset") ?? this.offset
          this.cursor = response.headers.get("electric-cursor") ?? this.cursor
          if (messages.some(message => message.headers.control === "up-to-date")) synced = true
          if (result === "commit") {
            const advanced = this.offset !== this.acknowledged.offset || this.handle !== this.acknowledged.handle
            this.acknowledged = { offset: this.offset, handle: this.handle, cursor: this.cursor }
            const target = response.headers.get("x-sync-revision")
            const status = target && compareRevision(target, this.state.manifest.revision) > 0 ? "updating" : "live"
            if (status !== this.state.status) this.setStatus(status)
            if (before !== this.state) this.emit()
            if (before.rows !== this.state.rows || advanced) this.persist()
          }
          failures = 0
        } catch {
          if (ac.signal.aborted) return
          failures++; this.status("paused")
          try { await this.fallback(ac.signal) } catch { /* Keep last complete data visibly paused. */ }
          // A fallback snapshot has no Electric offset. A future feed recovery
          // must replace the collection rather than replay against an old base.
          this.offset = "-1"; this.handle = null; this.cursor = null; synced = false; this.reset()
          this.acknowledged = { offset: "-1", handle: null, cursor: null }
          await new Promise<void>(resolve => {
            const done = () => { clearTimeout(timer); ac.signal.removeEventListener("abort", done); resolve() }
            const timer = setTimeout(done, Math.min(30_000, 1000 * 2 ** failures))
            ac.signal.addEventListener("abort", done, { once: true })
          })
        }
      }
    } catch { if (!ac.signal.aborted) this.status("paused") }
  }
}

export function useProjection(manifest: SyncManifest, initial?: SyncSnapshot) {
  const store = useMemo(() => {
    const key = `${manifest.viewer}:${manifest.scope}`
    let result = stores.get(key)
    if (!result) { result = new ProjectionStore(manifest, initial); stores.set(key, result) }
    // Bound in-memory account history as well as IndexedDB.
    if (stores.size > 12) for (const [id, old] of stores) {
      if (id !== key && old.references === 0) { stores.delete(id); if (stores.size <= 12) break }
    }
    return result
    // Identity/scope changes create a fresh collection; revisions seed it below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest.viewer, manifest.scope])
  const serverState = useMemo(() => ({ manifest, rows: initial?.rows ?? [], status: "connecting" as const, complete: !!initial }), [manifest, initial])
  const state = useSyncExternalStore(store.subscribe, store.snapshot, () => serverState)
  useReportSyncStatus(state.status, state.manifest.builtAt)
  useEffect(() => {
    if (activeViewer && activeViewer !== manifest.viewer) {
      for (const [key, old] of stores) if (old !== store) { old.deny(); stores.delete(key) }
      void clearSavedProjections().catch(() => {})
    }
    activeViewer = manifest.viewer
    if (initial) { store.seed(initial); store.emit() }
    return store.retain()
  }, [store, manifest.viewer, initial])
  return { ...state, reconcile: store.reconcile }
}
